/**
 * RetakeFeeLookupPage — standalone public page where a learner pastes
 * their CCCD and gets back: failed parts, total fee, bank account,
 * VietQR code (with amount + nội dung CK pre-filled).
 *
 * Layout (top → bottom):
 *   1. Hero with input + "Tra cứu" button
 *   2. QR block (large, bordered) — the primary CTA
 *   3. Details block — student info + failed-parts table + bank
 *
 * Renders different empty/error states based on the BE's tagged
 * status: notFound, passed, noAdminLink, noFeeConfig.
 */

import React, { useCallback, useState } from 'react';
import { useRetakeFeeLookup } from '../hooks/useRetakeFeeLookup';
import type { RetakeFeeLookupResult } from '../types';
import './RetakeFeeLookupPage.scss';

const ONLY_DIGITS = /[^\d]/g;

const RetakeFeeLookupPage: React.FC = () => {
  const { loading, error, result, lookup, reset } = useRetakeFeeLookup();
  const [cccd, setCccd] = useState('');
  const [copyHint, setCopyHint] = useState<string | null>(null);

  const onCccdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(ONLY_DIGITS, '').slice(0, 12);
    setCccd(cleaned);
  };

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      void lookup(cccd);
    },
    [cccd, lookup],
  );

  const onReset = () => {
    setCccd('');
    reset();
  };

  const onCopyTransfer = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyHint('Đã sao chép nội dung chuyển khoản.');
      window.setTimeout(() => setCopyHint(null), 2000);
    } catch {
      setCopyHint('Không sao chép được. Vui lòng chọn và sao chép thủ công.');
      window.setTimeout(() => setCopyHint(null), 3000);
    }
  };

  return (
    <div className="retake-fee-lookup">
      <section className="retake-fee-lookup__hero">
        <h1 className="retake-fee-lookup__title">Đóng phí thi lại</h1>
        <p className="retake-fee-lookup__subtitle">
          Nhập số CCCD (12 số) để tra cứu các phần thi cần đóng phí và
          nhận mã QR thanh toán.
        </p>

        <form className="retake-fee-lookup__form" onSubmit={onSubmit}>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            className="retake-fee-lookup__input"
            placeholder="Nhập số CCCD (12 chữ số)"
            value={cccd}
            onChange={onCccdChange}
            maxLength={12}
            aria-label="Số CCCD"
          />
          <button
            type="submit"
            className="retake-fee-lookup__submit"
            disabled={loading || cccd.length !== 12}
          >
            {loading ? 'Đang tra cứu…' : 'Tra cứu'}
          </button>
          {(result || error) && (
            <button
              type="button"
              className="retake-fee-lookup__reset"
              onClick={onReset}
            >
              Tra số khác
            </button>
          )}
        </form>

        {error && (
          <div className="retake-fee-lookup__error" role="alert">
            {error}
          </div>
        )}
      </section>

      {result && (
        <ResultBlock
          result={result}
          onCopyTransfer={onCopyTransfer}
          copyHint={copyHint}
        />
      )}
    </div>
  );
};

type ResultProps = {
  result: RetakeFeeLookupResult;
  onCopyTransfer: (text: string) => void;
  copyHint: string | null;
};

const ResultBlock: React.FC<ResultProps> = ({ result, onCopyTransfer, copyHint }) => {
  if (result.status === 'invalidCccd') {
    return (
      <EmptyState
        icon="error_outline"
        title="CCCD không hợp lệ"
        message="Vui lòng kiểm tra lại — CCCD phải gồm đúng 12 chữ số."
      />
    );
  }

  if (result.status === 'notFound') {
    return (
      <EmptyState
        icon="search_off"
        title="Không tìm thấy kết quả"
        message={`Hệ thống chưa có kết quả sát hạch nào cho CCCD ${result.cccd}. Vui lòng kiểm tra lại số hoặc liên hệ trung tâm.`}
      />
    );
  }

  if (result.status === 'passed') {
    return (
      <EmptyState
        icon="emoji_events"
        title="Chúc mừng — bạn đã đạt"
        message={`Học viên ${result.hoTen} đã đạt kỳ thi gần nhất (${result.kySatHach}). Không phát sinh phí thi lại.`}
      />
    );
  }

  if (result.status === 'noAdminLink') {
    return (
      <EmptyState
        icon="link_off"
        title="Kết quả thi cũ chưa liên kết trung tâm"
        message="Bản ghi này được nhập trước khi hệ thống tách phí theo từng trung tâm. Vui lòng liên hệ trực tiếp trung tâm để được hướng dẫn."
      />
    );
  }

  if (result.status === 'missingVehicleType') {
    return (
      <EmptyState
        icon="directions_car"
        title="Chưa xác định loại xe"
        message="Dữ liệu kết quả này chưa được phân loại Mô tô / Ô tô. Vui lòng liên hệ trung tâm để cập nhật."
      />
    );
  }

  if (result.status === 'dataIncomplete') {
    return (
      <EmptyState
        icon="warning_amber"
        title="Dữ liệu chưa đầy đủ"
        message="Hệ thống ghi nhận bạn chưa đạt nhưng chưa có thông tin chi tiết từng phần thi. Vui lòng liên hệ trung tâm để được xác nhận phí thi lại."
      />
    );
  }

  if (result.status === 'noFeeConfig') {
    return (
      <EmptyState
        icon="settings"
        title="Trung tâm chưa cấu hình mức phí thi lại"
        message="Vui lòng liên hệ trung tâm để được tư vấn mức phí và hướng dẫn thanh toán."
      />
    );
  }

  // status === 'ok'
  return (
    <>
      {/* QR block — primary CTA, placed directly under the hero */}
      <section className="retake-fee-lookup__qr-block">
        <div className="retake-fee-lookup__qr-card">
          {result.qrUrl ? (
            <img
              src={result.qrUrl}
              alt={`Mã QR thanh toán ${result.totalAmount.toLocaleString('vi-VN')} VNĐ`}
              className="retake-fee-lookup__qr-img"
            />
          ) : (
            <div className="retake-fee-lookup__qr-fallback">
              <i className="material-icons">qr_code_2</i>
              <p>Trung tâm chưa cấu hình tài khoản nhận. Vui lòng dùng thông tin chuyển khoản bên dưới.</p>
            </div>
          )}

          <p className="retake-fee-lookup__qr-caption">
            Quét mã QR bằng app ngân hàng để thanh toán
          </p>

          <div className="retake-fee-lookup__total">
            <span className="retake-fee-lookup__total-label">Tổng cộng</span>
            <span className="retake-fee-lookup__total-amount">
              {result.totalAmount.toLocaleString('vi-VN')}
              <span className="retake-fee-lookup__total-unit"> đ</span>
            </span>
          </div>

          <div className="retake-fee-lookup__qr-actions">
            {result.qrUrl && (
              <a
                href={result.qrUrl}
                download={`QR-thi-lai-${result.cccd}.png`}
                className="retake-fee-lookup__action-btn retake-fee-lookup__action-btn--secondary"
              >
                <i className="material-icons">download</i> Tải QR
              </a>
            )}
            <button
              type="button"
              className="retake-fee-lookup__action-btn retake-fee-lookup__action-btn--primary"
              onClick={() => onCopyTransfer(result.transferContent)}
            >
              <i className="material-icons">content_copy</i> Sao chép nội dung CK
            </button>
          </div>

          {copyHint && (
            <div className="retake-fee-lookup__copy-hint" role="status">
              {copyHint}
            </div>
          )}
        </div>
      </section>

      {/* Details block — student info + failed parts + bank info */}
      <section className="retake-fee-lookup__details-block">
        <h2 className="retake-fee-lookup__details-title">Thông tin chi tiết</h2>

        <div className="retake-fee-lookup__student-grid">
          <InfoRow label="Họ tên" value={result.hoTen} />
          <InfoRow label="CCCD" value={result.cccd} />
          <InfoRow label="Loại xe" value={result.vehicleType === 'moto' ? 'Mô tô' : result.vehicleType === 'oto' ? 'Ô tô' : '—'} />
          <InfoRow label="Kỳ sát hạch" value={result.kySatHach} />
          <InfoRow label="Ngày SH" value={result.ngaySH} />
        </div>

        <h3 className="retake-fee-lookup__details-subtitle">
          Các phần thi cần đóng phí
        </h3>
        <div className="retake-fee-lookup__table-wrap">
          <table className="retake-fee-lookup__table">
            <thead>
              <tr>
                <th>Phần thi</th>
                <th className="retake-fee-lookup__col-num">Điểm</th>
                <th className="retake-fee-lookup__col-num">Phí (VNĐ)</th>
              </tr>
            </thead>
            <tbody>
              {result.breakdown.map((row) => (
                <tr key={row.partCode}>
                  <td>{row.partName}</td>
                  <td className="retake-fee-lookup__col-num">{row.diem ?? '—'}</td>
                  <td className="retake-fee-lookup__col-num">
                    {row.amount.toLocaleString('vi-VN')}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>
                  <strong>Tổng cộng</strong>
                </td>
                <td className="retake-fee-lookup__col-num retake-fee-lookup__total-cell">
                  {result.totalAmount.toLocaleString('vi-VN')}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {result.bank && (
          <div className="retake-fee-lookup__bank">
            <h3 className="retake-fee-lookup__details-subtitle">
              Tài khoản nhận thanh toán
            </h3>
            <InfoRow label="Ngân hàng" value={result.bank.bankName} />
            <InfoRow label="Số tài khoản" value={result.bank.accountNumber} mono />
            <InfoRow label="Chủ tài khoản" value={result.bank.accountHolder} />
            <InfoRow label="Nội dung CK" value={result.transferContent} mono />
          </div>
        )}
      </section>
    </>
  );
};

const InfoRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <div className="retake-fee-lookup__info-row">
    <span className="retake-fee-lookup__info-label">{label}</span>
    <span
      className={`retake-fee-lookup__info-value${mono ? ' retake-fee-lookup__info-value--mono' : ''}`}
    >
      {value}
    </span>
  </div>
);

const EmptyState: React.FC<{ icon: string; title: string; message: string }> = ({
  icon,
  title,
  message,
}) => (
  <section className="retake-fee-lookup__empty">
    <i className="material-icons retake-fee-lookup__empty-icon">{icon}</i>
    <h2 className="retake-fee-lookup__empty-title">{title}</h2>
    <p className="retake-fee-lookup__empty-message">{message}</p>
  </section>
);

export default RetakeFeeLookupPage;
